var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isNotNilOrEmpty, isNilOrEmpty } from '@pcty/utils';
import { useLocalization, useTheme } from '@citrus/user';
import { Tag } from '@citrus/tag';
import { TagContainerStyles } from '../../Select.styled';
import isEqual from 'lodash/isEqual';
import { getAutomation, useSize } from '@citrus/runtime';
import localizationMap from '../../../localization';
import { checkHowManyFit, getWindowSize } from '../utils/useKeyPress';
/**
 * this will return an empty react partial if !multi.  this is to avoid a changing in the order of react hooks.
 * @param actionAreaId this string references the id of the input action area to inject the tags
 */
var MultiTags = function (_a) {
    var actionAreaRef = _a.actionAreaRef, disabled = _a.disabled, fixedOptions = _a.fixedOptions, handleRowClick = _a.handleRowClick, id = _a.id, readOnly = _a.readOnly, isMulti = _a.isMulti, options = _a.options, value = _a.value, automationId = _a.automationId;
    var localization = useLocalization(localizationMap);
    var citrus2022 = useTheme().citrus2022;
    var isCitron = citrus2022 !== null && citrus2022 !== void 0 ? citrus2022 : false;
    var _b = useState(getWindowSize()), windowSize = _b[0], setWindowSize = _b[1];
    var _c = useState([]), tagsArray = _c[0], setTagsArray = _c[1];
    var _d = useState(false), isLoaded = _d[0], setIsLoaded = _d[1];
    var tagContainer = useRef(null);
    useEffect(function () {
        setIsLoaded(true);
    }, []);
    useEffect(function () {
        if (!isLoaded)
            return;
        setTagsArray([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMulti]);
    var containerSize = useSize(actionAreaRef);
    useLayoutEffect(function () {
        if (!isMulti)
            return;
        // timeoutId for debounce mechanism
        var timeoutId;
        var resizeListener = function () {
            // prevent execution of previous setTimeout
            clearTimeout(timeoutId);
            // change width from the state object after 150 milliseconds
            timeoutId = setTimeout(function () { return setWindowSize(getWindowSize()); }, 250);
        };
        // set resize listener
        window.addEventListener('resize', resizeListener);
        // clean up function
        return function () {
            // remove resize listener
            window.removeEventListener('resize', resizeListener);
        };
    }, [isMulti]);
    useEffect(function () {
        // if we have options selected and the actionarea is active
        if (isMulti && (isNotNilOrEmpty(value) || isNotNilOrEmpty(fixedOptions)) && (actionAreaRef === null || actionAreaRef === void 0 ? void 0 : actionAreaRef.current)) {
            getTags({ value: value }, actionAreaRef);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, windowSize, isMulti, disabled, actionAreaRef, containerSize.width]);
    var getTagsContainer = function () {
        var paddingStyles = __assign({}, (value.length === 0 && { padding: 0 }));
        return (React.createElement(TagContainerStyles, { ref: tagContainer, style: paddingStyles }, tagsArray));
    };
    var handleTagClose = function (x) {
        var clickIndex = options.findIndex(function (obj) { return isEqual(x, obj); });
        handleRowClick(clickIndex, x);
    };
    /**
     * will measure and return the tags that fit into the input action area
     */
    var getTags = function (_a, actionAreaRef) {
        var value = _a.value;
        // this takes in the fixed options, and always sets them to the front, and removes any duplicates
        var dedupedValue = Array.from(new Set(__spreadArray(__spreadArray([], fixedOptions, true), value, true))).filter(function (x) { return x && !x.disabled && !x.header && !x.empty; });
        // const inputActionArea = document.getElementById(actionAreaId);
        var howManyFit = checkHowManyFit({
            childArray: dedupedValue,
            parent: tagContainer.current,
            grandParent: actionAreaRef.current,
            moreText: localization.more,
            isCitron: isCitron,
        });
        var handleTagKeyClose = function (e, x) {
            if (e.key === 'Enter' || e.code === 'Enter') {
                e.stopPropagation();
                e.preventDefault();
                return handleTagClose(x);
            }
        };
        var onMouseDown = function (e) {
            e.preventDefault();
            e.stopPropagation();
        };
        /**
         * how many tags fit minus the 'more' button
         */
        var newTagsArray = howManyFit.map(function (x, i) {
            var tagItemAutomationId = getAutomation(automationId, "tag-item-".concat(i))['data-automation-id'];
            if (x.isFixed) {
                return (React.createElement(Tag, { automationId: tagItemAutomationId, disabled: disabled, key: i, onMouseDown: onMouseDown, type: "gray", value: x.label }));
            }
            return (React.createElement(Tag, { automationId: tagItemAutomationId, disabled: disabled, key: i, onClickClose: x && !x.header && !disabled && !readOnly ? function () { return handleTagClose(x); } : undefined, onKeyDown: x && !x.header && !disabled && !readOnly ? function (e) { return handleTagKeyClose(e, x); } : undefined, onMouseDown: onMouseDown, tabIndex: 0, type: "pill", value: x.label }));
        });
        // add the more button if needed
        if (dedupedValue && newTagsArray.length !== dedupedValue.length) {
            newTagsArray.push(React.createElement(Tag, { disabled: disabled, key: "".concat(id, "-select-more-button"), onMouseDown: onMouseDown, type: 'pill', value: "+".concat(dedupedValue.length - newTagsArray.length, " ").concat(localization.more) }));
        }
        setTagsArray(newTagsArray);
    };
    if (!isMulti || (isMulti && isNilOrEmpty(value) && isNilOrEmpty(fixedOptions))) {
        return React.createElement(React.Fragment, null);
    }
    return getTagsContainer();
};
export default MultiTags;
